import { IncomingMessage } from "http";
import {
  fetchQuery,
  IEnvironment,
  INetwork,
  OperationType,
} from "relay-runtime";

import { getCommunityConfig } from "@framework/config/community/community.config";
import createEnvironment from "@framework/relay/shared/createEnvironment";
import { HydrationData } from "@framework/relay/shared/hydration";
import {
  GraphQLOperation,
  IncomingRequestContext,
} from "@framework/relay/shared/Middleware";
import { isEqual } from "@framework/relay/utils";
import {
  bypassWpCacheEnabled,
  getQueryResponseSurrogateKeys,
} from "@framework/utils/cache.utils";
import { getDurationHeaderKeyForOperation } from "@framework/utils/header.utils";
import {
  getExecutionDuration,
  isomorphicTimer,
} from "@voxmedia/duet-utils/logging";

import {
  FetchQueryOptions,
  FetchQueryReturnType,
  CreateNetworkFunction,
  SSRQueryFetcher,
} from "./query-fetcher.types";
import {
  extractRequestHeaderData,
  getNextInternalRequestMeta,
  getRequestIdentifier,
  getConcreteRequest,
} from "./query-fetcher.utils";

/**
 * Internal: build a Relay environment for this request using the injected network factory.
 */
const buildEnvironment = ({
  hydration,
  persisted,
  req,
  communityId,
  createServerNetworkFunction,
  onHeaders,
}: {
  hydration: HydrationData;
  persisted: Record<string, string>;
  req: IncomingMessage;
  communityId: number;
  createServerNetworkFunction: () => CreateNetworkFunction;
  onHeaders: (headers: Headers, op: GraphQLOperation) => void;
}): IEnvironment => {
  const { communityApiUrl } = getCommunityConfig(communityId);

  const { forwardedHost, ...resourceData } = extractRequestHeaderData(
    req.headers,
  );
  const nextInternalRequestMeta = getNextInternalRequestMeta(req);

  const incomingRequestContext: IncomingRequestContext = {
    communityId,
    url: req.url,
    forwardedHost,
    resourceData,
    nextInternalRequestMeta,
  };

  const network: INetwork = createServerNetworkFunction()({
    hydrationData: hydration,
    persisted,
    communityApiUrl,
    userAgent: req.headers["user-agent"],
    bypassCache: bypassWpCacheEnabled(req),
    options: { onHeaders },
    incomingRequestContext,
  });

  return createEnvironment(network);
};

/**
 * Functional core: create a per-request query fetcher with minimal, localized state.
 */
export const createSSRQueryFetcher = ({
  req,
  persisted,
  communityId,
  createServerNetworkFunction,
}: {
  req: IncomingMessage;
  persisted: Record<string, string>;
  communityId: number;
  createServerNetworkFunction: () => CreateNetworkFunction;
}): SSRQueryFetcher => {
  // state captured in closure
  const hydration: HydrationData = { responses: [] };
  const responseHeaders: Record<string, Record<string, string>> = {};
  const isApiRoute = req.url?.startsWith("/api/") ?? false;

  // header collector from middleware
  const onHeaders = (headers: Headers, operation: GraphQLOperation) => {
    const id = getRequestIdentifier(
      operation.operationName,
      operation.variables,
    );
    const collected: Record<string, string> = {};
    headers.forEach((value, key) => (collected[key] = value));
    responseHeaders[id] = collected;
  };

  const environment = buildEnvironment({
    hydration,
    persisted,
    req,
    communityId,
    createServerNetworkFunction,
    onHeaders,
  });

  async function fetchQueryFn<TQuery extends OperationType>(
    opts: FetchQueryOptions<TQuery>,
  ): Promise<FetchQueryReturnType<TQuery>> {
    const { query, variables, durationHeaderKey, throwOnError } = opts;
    const duetPrefix = opts.duetPrefix ?? !isApiRoute;
    const start = isomorphicTimer();

    const response = await fetchQuery<TQuery>(environment, query, variables, {
      fetchPolicy: "network-only",
      networkCacheConfig: { metadata: { throwOnError } },
    }).toPromise();

    const duration = getExecutionDuration(start);
    const operationName = getConcreteRequest(query).operation.name;
    const identifier = getRequestIdentifier(operationName, variables);

    // merge collected headers and inject timing header
    const headers =
      identifier in responseHeaders
        ? { ...responseHeaders[identifier], "vm-duration-gql-query": duration }
        : { "vm-duration-gql-query": duration };

    const surrogateKeys = getQueryResponseSurrogateKeys(headers, "gql", {
      duetPrefix,
    });

    const discardHydration = () => {
      const idx = hydration.responses.findIndex(
        (v) =>
          v.operationName === operationName && isEqual(v.variables, variables),
      );
      if (idx !== -1) {
        hydration.responses.splice(idx, 1);
      }
    };

    return {
      // keep the response opaque (only fragment spreads surface if present)
      response: response as FetchQueryReturnType<TQuery>["response"],
      headers,
      surrogateKeys,
      durationHeaderKey:
        durationHeaderKey || getDurationHeaderKeyForOperation(operationName),
      duration,
      discardHydration,
    };
  }

  return {
    fetchQuery: fetchQueryFn,
    hydration,
    getCollectedHeaders: () => Object.freeze({ ...responseHeaders }),
  };
};
